import React from 'react';
import { Settings, ChevronUp } from 'lucide-react';
import '../../styles/MobileScreenerContextBar.css';

/**
 * MobileScreenerContextBar — fixed 52px chrome above the MobileBottomNav.
 *
 * Two chips:
 *   1. Status chip — shows the simulated exit date / % of exp / per-contract
 *      state derived from daysToExitMode + (daysToExit | daysToExitPercent).
 *   2. Settings chip — "Heatmap & filter settings" — opens the existing
 *      settings + filters sheet.
 *
 * Both chips call onOpenSheet (single sheet for now; per-section sheets are
 * a deferred follow-up).
 *
 * Props:
 *   isMobile, hasResults        — render gates
 *   scrolledPastHeader          — render gate; true once the .mhm-page-header
 *                                 has reached the viewport from below (i.e., the
 *                                 banner is at-or-above the viewport bottom), so
 *                                 the bar appears as soon as the user enters the
 *                                 heatmap section and stays visible while they
 *                                 remain at or past it. Hidden only while the
 *                                 user is still on the form above the heatmap.
 *   daysToExitMode              — 'fixed' | 'percent' | 'per-contract'
 *   daysToExit                  — number, used when mode === 'fixed'
 *   daysToExitPercent           — 0-1 float, used when mode === 'percent'
 *   onOpenSheet()               — opens the heatmap settings sheet
 */
function buildStatusContent(daysToExitMode, daysToExit, daysToExitPercent) {
  if (daysToExitMode === 'percent' && daysToExitPercent != null) {
    const pct = Math.round(daysToExitPercent * 100);
    return { label: '% of exp:', value: `${pct}%` };
  }
  if (daysToExitMode === 'fixed' && daysToExit != null && !isNaN(Number(daysToExit))) {
    const d = new Date();
    d.setDate(d.getDate() + Number(daysToExit));
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return { label: 'Exit:', value: `${yyyy}-${mm}-${dd} (${daysToExit} d.)` };
  }
  return { label: 'Exit:', value: 'Per-contract' };
}

export default function MobileScreenerContextBar({
  isMobile,
  hasResults,
  scrolledPastHeader,
  daysToExitMode,
  daysToExit,
  daysToExitPercent,
  onOpenSheet,
}) {
  if (!isMobile || !hasResults || !scrolledPastHeader) return null;

  const status = buildStatusContent(daysToExitMode, daysToExit, daysToExitPercent);

  return (
    <nav className="mscb-root" aria-label="Heatmap quick settings">
      <button
        type="button"
        className="mscb-chip"
        onClick={onOpenSheet}
        aria-label={`${status.label} ${status.value}. Tap to change.`}
      >
        <span className="mscb-chip-label">{status.label}</span>
        <span className="mscb-chip-value">{status.value}</span>
        <ChevronUp className="mscb-chip-caret" />
      </button>

      <button
        type="button"
        className="mscb-chip mscb-chip-settings"
        onClick={onOpenSheet}
        aria-label="Heatmap and filter settings"
      >
        <span className="mscb-chip-value">Heatmap &amp; filter settings</span>
        <Settings className="mscb-chip-icon" />
      </button>
    </nav>
  );
}
